import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ExerciseResult } from './lessonReducer.js';

/**
 * In-progress lesson sessions — what the user has done in a lesson they
 * haven't finished yet. Persisted to localStorage so a refresh / app close
 * resumes from the same exercise. Cleared when the lesson completes
 * (`complete_lesson` RPC fires) or the user restarts.
 *
 * Phase 9 will migrate this to Dexie/IndexedDB to share storage with the
 * offline pending-completion queue. For now, localStorage is enough — these
 * are tiny records (≤2 KB per active lesson) and we only ever have one
 * active lesson per user.
 *
 * Why save `index` and `results` (not the whole reducer state)?
 *   - `phase` is always `answering` on resume (we never persist `checked` —
 *     that mid-state would force the user to re-tap "continue" on resume).
 *   - `wrongOnCurrent` and `lastVerdict` are transient feedback, useless on
 *     resume.
 *   - `startedAt` is preserved so the elapsed-time stat is still honest if
 *     they resume mid-session.
 */

export interface LessonSession {
  lessonId: string;
  /** Index of the next-to-answer exercise — i.e. how far the user got. */
  index: number;
  /** Per-exercise log so XP/accuracy/perfect-run carry across resume. */
  results: ExerciseResult[];
  /** Original lesson start. Preserved across resume for elapsed-time stat. */
  startedAt: number;
  /** Last time we wrote to disk. Useful for stale-session GC later. */
  savedAt: number;
}

interface SessionState {
  /** Map of lessonId → session. */
  sessions: Record<string, LessonSession>;
  saveSession: (session: LessonSession) => void;
  clearSession: (lessonId: string) => void;
  /** Drop sessions older than `cutoffMs`. Defaults to 30 days. */
  pruneStale: (cutoffMs?: number) => void;
}

const STORE_KEY = 'fluent.lesson-sessions.v1';
const DEFAULT_STALE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      sessions: {},
      saveSession: (session) =>
        set((s) => ({
          sessions: { ...s.sessions, [session.lessonId]: session },
        })),
      clearSession: (lessonId) =>
        set((s) => {
          if (!s.sessions[lessonId]) return s;
          const next = { ...s.sessions };
          delete next[lessonId];
          return { sessions: next };
        }),
      pruneStale: (cutoffMs = DEFAULT_STALE_MS) => {
        const cutoff = Date.now() - cutoffMs;
        const fresh: Record<string, LessonSession> = {};
        for (const [id, sess] of Object.entries(get().sessions)) {
          if (sess.savedAt >= cutoff) fresh[id] = sess;
        }
        set({ sessions: fresh });
      },
    }),
    {
      name: STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

/** Selector: read-only snapshot of the in-progress sessions. */
export function useInProgressSessions(): Record<string, LessonSession> {
  return useSessionStore((s) => s.sessions);
}

/** Direct accessor (non-React) for a single lesson — used in LessonPlayer
 *  init reducer where hooks aren't available before mount. */
export function getSession(lessonId: string): LessonSession | null {
  return useSessionStore.getState().sessions[lessonId] ?? null;
}

export function saveSession(session: LessonSession): void {
  useSessionStore.getState().saveSession(session);
}

export function clearSession(lessonId: string): void {
  useSessionStore.getState().clearSession(lessonId);
}
